<template>
  <div class="flex flex-col h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white border-b px-4 lg:px-6 py-3 lg:py-4 flex-shrink-0">
      <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <button @click="goBack" class="text-gray-600 hover:text-gray-800 text-sm lg:text-base">
            <font-awesome-icon icon="arrow-left" class="small" />
            <span class="hidden sm:inline">Zurück zur Tabelle</span>
            <span class="sm:hidden">Zurück</span>
          </button>
          <button v-if="confirmOrigin" @click="goBackToContract" class="text-gray-600 hover:text-gray-800 text-sm lg:text-base">
            <font-awesome-icon icon="arrow-left" class="small" />
            <span class="hidden sm:inline">Zurück zum Vertrag</span>
            <span class="sm:hidden">Vertrag</span>
          </button>
          <h1 class="text-lg lg:text-2xl font-bold">
            {{ customer.id ? `Kunde #${customer.id} bearbeiten` : 'Neuen Kunden anlegen' }}
          </h1>
        </div>
        <div class="flex flex-col sm:flex-row gap-2">
          <button
            @click="saveCustomer"
            :disabled="saving"
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 text-sm lg:text-base"
          >
            {{ saving ? 'Speichert...' : 'Speichern' }}
          </button>
          <button
            @click="saveAndClose"
            :disabled="saving"
            class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 text-sm lg:text-base"
          >
            <span class="hidden sm:inline">Speichern und schließen</span>
            <span class="sm:hidden">Speichern & Schließen</span>
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div class="container mx-auto p-4 lg:p-6 flex flex-col lg:flex-row gap-4 lg:gap-6">
      <!-- Left table of contents (desktop only) -->
      <nav class="hidden lg:block lg:w-56 xl:w-64 flex-shrink-0" aria-label="Kundennavigation">
        <div class="sticky top-4 max-h-[calc(100vh-10rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
          <div class="flex items-center justify-between px-3 pb-2 pt-1">
            <div>
              <div class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Kunde</div>
              <div class="mt-0.5 text-sm font-semibold text-gray-800">Inhalt</div>
            </div>
            <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{{ navSections.length }}</span>
          </div>

          <div class="space-y-0.5 border-t border-gray-100 pt-2">
            <button
              v-for="item in navSections"
              :key="item.id"
              @click="scrollToSection(item.id)"
              class="group flex w-full items-center gap-3 !border-0 !bg-transparent !px-3 !py-2 text-left text-sm !font-medium !shadow-none transition-colors duration-150 !text-gray-600 hover:!bg-gray-50 hover:!text-gray-900"
            >
              <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400 transition-colors group-hover:bg-gray-200 group-hover:text-gray-600">
                <font-awesome-icon v-if="item.icon" :icon="item.icon" class="text-xs" />
              </span>
              <span class="min-w-0 leading-5">{{ item.label }}</span>
            </button>
          </div>
        </div>
      </nav>
      <div class="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-4 lg:space-y-6">
          
          <!-- Contracts Section (if customer exists) -->
          <div v-if="customer.id" ref="section-contracts" class="bg-white rounded-lg shadow p-4 lg:p-6">
            <div class="flex items-center justify-between mb-4 gap-2">
              <h2 class="flex-1 text-lg lg:text-xl font-semibold cursor-pointer select-none flex items-center justify-between" @click="toggleSection('contracts')">
                <span>Verträge</span>
                <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.contracts }" />
              </h2>
              <button
                @click="createContract"
                class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Vertrag anlegen
              </button>
            </div>
            <div v-show="!collapsedSections.contracts">
            <div v-if="contracts.length > 0" class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left">Kind</th>
                    <th class="px-4 py-2 text-left">Projekt</th>
                    <th class="px-4 py-2 text-left">Status</th>
                    <th class="px-4 py-2 text-left">Laufzeit</th>
                    <th class="px-4 py-2 text-left">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="contract in contracts" :key="contract.id" class="border-t">
                    <td class="px-4 py-2">{{ contract.Vorname }} {{ contract.Nachname }}</td>
                    <td class="px-4 py-2">{{ contract.Projekt?.Titel }}</td>
                    <td class="px-4 py-2">{{ contract.Status }}</td>
                    <td class="px-4 py-2">{{ formatDate(contract.Vertragsbeginn) }} - {{ formatDate(contract.Vertragsende) }}</td>
                    <td class="px-4 py-2">
                      <button
                        @click="editContract(contract.id)"
                        class="text-blue-500 hover:text-blue-700"
                        title="Bearbeiten"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        @click="editContractNew(contract.id)"
                        class="text-purple-600 hover:text-purple-800 ml-2"
                        title="Bearbeiten (modern)"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-gray-500 py-8">
              Keine Verträge vorhanden
            </div>
            </div>
          </div>

          <!-- Customer Data Form -->
          <div ref="section-stammdaten" class="bg-white rounded-lg shadow p-4 lg:p-6">
            <h2 class="text-lg lg:text-2xl font-semibold mb-4 lg:mb-6 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('stammdaten')">
              <span>Stammdaten</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.stammdaten }" />
            </h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4" v-show="!collapsedSections.stammdaten">
              <!-- ID (read-only) -->
              <div>
                <label class="block small mb-1">ID</label>
                <div class="font-bold text-gray-900">
                  {{ customer.id || 'Wird beim Speichern erzeugt' }}
                </div>
              </div>

              <!-- Status -->
              <div>
                <label class="block small mb-1">Status</label>
                <select v-model="customer.Status" class="w-full border rounded px-3 py-2">
                  <option value="">Bitte auswählen</option>
                  <option value="Neu">Neu</option>
                  <option value="Geändert">Geändert</option>
                  <option value="Bestätigt">Bestätigt</option>
                </select>
              </div>

              <!-- Ehrenamtsstatus -->
              <div v-if="customer.Ehrenamtsstatus">
                <label class="block small mb-1">Ehrenamtsstatus</label>
                <select v-model="customer.Ehrenamtsstatus" class="w-full border rounded px-3 py-2">
                  <option value="">Bitte auswählen</option>
                  <option value="Angefragt">Angefragt</option>
                  <option value="Abgelehnt">Abgelehnt</option>
                  <option value="Bestätigt">Bestätigt</option>
                </select>
              </div>

              <!-- Erziehungsberechtigter 1 -->
              <div>
                <label class="block small mb-1">Vorname Erziehungsberechtigter</label>
                <input v-model="customer.VornameErziehungsberechtigter" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Nachname Erziehungsberechtigter</label>
                <input v-model="customer.NachnameErziehungsberechtigter" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Geburtsdatum -->
              <div>
                <label class="block small mb-1">Geburtsdatum</label>
                <input v-model="customer.Geburtsdatum" type="date" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Email & Phone 1 -->
              <div>
                <label class="block small mb-1">E-Mail Eltern</label>
                <input v-model="customer.EmailEltern" type="email" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Mobilnummer Eltern</label>
                <input v-model="customer.MobilnummerEltern" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Erziehungsberechtigter 2 -->
              <div>
                <label class="block small mb-1">Vorname Erziehungsberechtigter 2</label>
                <input v-model="customer.VornameErziehungsberechtigter2" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Nachname Erziehungsberechtigter 2</label>
                <input v-model="customer.NachnameErziehungsberechtigter2" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Email & Phone 2 -->
              <div>
                <label class="block small mb-1">E-Mail Eltern 2</label>
                <input v-model="customer.EmailEltern2" type="email" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Mobilnummer Eltern 2</label>
                <input v-model="customer.MobilnummerEltern2" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Address -->
              <div>
                <label class="block small mb-1">Straße</label>
                <input v-model="customer.Strasse" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Hausnummer</label>
                <input v-model="customer.Hausnummer" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Postleitzahl</label>
                <input v-model="customer.Postleitzahl" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Ort</label>
                <input v-model="customer.Ort" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Festnetz</label>
                <input v-model="customer.Festnetz" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Banking Information -->
              <div>
                <label class="block small mb-1">IBAN</label>
                <input v-model="customer.IBAN" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">BIC</label>
                <input v-model="customer.BIC" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Kontonummer</label>
                <input v-model="customer.Kontonummer" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Bankleitzahl</label>
                <input v-model="customer.Bankleitzahl" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Abw. Kontoinhaber</label>
                <input v-model="customer.AbwKontoinhaber" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Fibu Kontonummer</label>
                <input v-model="customer.FibuKontonummer" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Kreditor-Nummer</label>
                <input v-model="customer.KreditorNummer" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- BUT Information -->
              <div>
                <label class="block small mb-1">BUT</label>
                <input v-model="customer.BUT" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Art</label>
                <input v-model="customer.Art" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">BUT Laufzeit</label>
                <input v-model="customer.BUTLaufzeit" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Aktenzeichen</label>
                <input v-model="customer.Aktenzeichen" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">KölnPass</label>
                <input v-model="customer.KoelnPass" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <!-- Additional Fields -->
              <div>
                <label class="block small mb-1">Buchungszeitraum I</label>
                <input v-model="customer.BuchungszeitraumI" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Buchungszeitraum II</label>
                <input v-model="customer.BuchungszeitraumII" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Teilnehmerbetrag</label>
                <input v-model="customer.Teilnehmerbetrag" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Kostenstelle</label>
                <input v-model="customer.Kostenstelle" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Eingang</label>
                <input v-model="customer.Eingang" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Zustimmung Veröffentlichung 1</label>
                <input v-model="customer.ZustimmungVeroeffentlichung1" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Zustimmung Veröffentlichung 2</label>
                <input v-model="customer.ZustimmungVeroeffentlichung2" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Abholung Notwendig</label>
                <input v-model="customer.AbholungNotwendig" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Adresstyp</label>
                <input v-model="customer.Adresstyp" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Kennz Hauptbankverbindung</label>
                <input v-model="customer.KennzHauptbankverbindung" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Lastschrift</label>
                <input v-model="customer.Lastschrift" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Mandantenbank</label>
                <input v-model="customer.Mandantenbank" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label class="block small mb-1">Länderkennzeichen</label>
                <input v-model="customer.Laenderkennzeichen" type="text" class="w-full border rounded px-3 py-2" />
              </div>

              <div class="md:col-span-2">
                <label class="block small mb-1">Bemerkung</label>
                <textarea v-model="customer.Bemerkung" rows="3" class="w-full border rounded px-3 py-2"></textarea>
              </div>

              <!-- Boolean Fields -->
              <div class="flex items-center">
                <input v-model="customer.Alleinerziehend" type="checkbox" id="alleinerziehend" class="small" />
                <label for="alleinerziehend" class="small">Alleinerziehend</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.Bestand" type="checkbox" id="bestand" class="small" />
                <label for="bestand" class="small">Bestand</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.Zuschussberechtigt" type="checkbox" id="zuschussberechtigt" class="small" />
                <label for="zuschussberechtigt" class="small">Zuschussberechtigt</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.Datenschutz" type="checkbox" id="datenschutz" class="small" />
                <label for="datenschutz" class="small">Datenschutz</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.Fotoerklaerung" type="checkbox" id="fotoerklaerung" class="small" />
                <label for="fotoerklaerung" class="small">Fotoerklärung</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.ProfilKomplett" type="checkbox" id="profilkomplett" class="small" />
                <label for="profilkomplett" class="small">Profil Komplett</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.AdresseBestaetigt" type="checkbox" id="adressebestaetigt" class="small" />
                <label for="adressebestaetigt" class="small">Adresse Bestätigt</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.NutztApp" type="checkbox" id="nutztapp" class="small" />
                <label for="nutztapp" class="small">Nutzt App</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.admin" type="checkbox" id="admin" class="small" />
                <label for="admin" class="small">Admin</label>
              </div>

              <div class="flex items-center">
                <input v-model="customer.KeineInfomails" type="checkbox" id="keineinfomails" class="small" />
                <label for="keineinfomails" class="small">Keine Infomails</label>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="space-y-4 lg:space-y-6">
          <!-- Actions Section -->
          <div v-if="customer.id" ref="section-aktionen" class="bg-white rounded-lg shadow p-4 lg:p-6">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('aktionen')">
              <span>Aktionen</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.aktionen }" />
            </h2>
            <div class="space-y-3" v-show="!collapsedSections.aktionen">
              <button
                v-if="customer.Status !== 'Bestätigt'"
                @click="confirmCustomer"
                class="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center gap-2"
              >
                <font-awesome-icon icon="check-circle" />
                Kunden bestätigen
              </button>
              <button
                v-if="customer.Ehrenamtsstatus && customer.Ehrenamtsstatus !== 'Bestätigt'"
                @click="confirmEhrenamtsstatus(false)"
                class="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-2"
              >
                <font-awesome-icon icon="check-circle" />
                Ehrenamtsstatus bestätigen (mit Mail)
              </button>
              <button
                v-if="customer.Ehrenamtsstatus && customer.Ehrenamtsstatus !== 'Bestätigt'"
                @click="confirmEhrenamtsstatus(true)"
                class="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <font-awesome-icon icon="check-circle" />
                Ehrenamtsstatus bestätigen (Mail bearbeiten)
              </button>
              <button
                @click="assumeIdentity"
                class="w-full px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center justify-center gap-2"
                title="Als dieser Kunde anmelden"
              >
                <font-awesome-icon icon="user-secret" />
                Identität annehmen
              </button>
            </div>
          </div>

          <!-- Ehrenamt Forms Section -->
          <div v-if="customer.Ehrenamtsstatus && allEhrenamtFormTypes.length > 0" ref="section-ehrenamt" class="bg-white rounded-lg shadow p-4 lg:p-6">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('ehrenamt')">
              <span>Ehrenamt Formulare</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.ehrenamt }" />
            </h2>
            <div class="space-y-3" v-show="!collapsedSections.ehrenamt">
              <div v-for="formType in allEhrenamtFormTypes" :key="formType" class="flex items-center justify-between border-b pb-2">
                <div class="flex items-center gap-2">
                  <span class="w-3 h-3 rounded-full inline-block flex-shrink-0" :class="isFormDone(formType) ? 'bg-green-500' : 'bg-gray-300'"></span>
                  <span class="font-medium">{{ formType }}</span>
                </div>
                <div v-if="isFormDone(formType)" class="flex items-center gap-2">
                  <span class="text-green-600 font-bold">Ausgefüllt</span>
                  <a :href="`${beUrl}/ehrenamt/forms/${getFormId(formType)}/view`" target="_blank" class="text-sm text-blue-600 hover:underline ml-2" title="Ansehen">
                    <font-awesome-icon icon="eye" />
                  </a>
                  <button @click="resetForm(formType)" class="text-red-600 border-none hover:text-red-800 ml-2" title="Reset">
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
                <div v-else class="flex items-center gap-2">
                  <span v-if="availableFormConfigs[formType] && availableFormConfigs[formType].requiresSignature && !availableFormConfigs[formType].internal" class="text-red-500 font-bold">Fehlt</span>
                  <span v-else-if="availableFormConfigs[formType] && availableFormConfigs[formType].internal" class="text-orange-500 font-bold">Intern</span>
                  <span v-else class="text-gray-500 font-bold">Optional</span>
                  <button v-if="availableFormConfigs[formType] && (!availableFormConfigs[formType].requiresSignature || availableFormConfigs[formType].internal)" @click="openFormFiller(formType)" class="text-sm text-blue-600 hover:text-blue-800 border-none bg-transparent px-2 py-1 rounded hover:bg-blue-50" title="Ausfüllen">
                    <font-awesome-icon icon="pen" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- E-Mails -->
          <div ref="section-emails" class="bg-white rounded-lg shadow p-4 lg:p-6">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('emails')">
              <span>E-Mails</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.emails }" />
            </h2>
            <div v-show="!collapsedSections.emails">
            <div v-if="sentMails.length === 0" class="text-gray-500 text-center py-4">
              Keine E-Mails gesendet
            </div>
            <div v-else class="space-y-2 max-h-96 overflow-y-auto">
              <div v-for="mail in sentMails" :key="mail.id" class="border rounded p-3">
                <div class="flex justify-between items-center">
                  <div>
                    <div class="font-medium">{{ mail.subject }}</div>
                    <div class="text-sm text-gray-500">
                      <span>Vorlage: {{ mail.templateName }}</span>
                      <span class="mx-2">•</span>
                      <span>{{ mail.toUser }}</span>
                    </div>
                    <div class="text-sm text-gray-600">
                      <span>Vertrag: {{ mail.contract }}</span>
                    </div>
                    <div class="text-xs text-gray-400">{{ mail.date }}</div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- History Section -->
          <div ref="section-history" class="bg-white rounded-lg shadow p-4 lg:p-6">
            <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('history')">
              <span>Letzte Änderungen</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.history }" />
            </h2>
            <div v-show="!collapsedSections.history">
            <div v-if="meaningfulHistory.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
              <div v-for="(version, index) in meaningfulHistory" :key="version.id" class="border-l-2 border-blue-400 pl-3">
                <div class="text-sm">
                  <div class="font-medium text-gray-900">{{ formatFieldName(version.field) }}</div>
                  <div class="text-gray-600">
                    <span class="line-through">{{ formatValue(version.oldValue, version.field) }}</span>
                    →
                    <span class="text-green-600">{{ formatValue(version.newValue, version.field) }}</span>
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ formatDateTime(version.createdAt) }} von {{ version.changedBy }}
                  </div>
                </div>
                <div v-if="index < meaningfulHistory.length - 1" class="border-b border-gray-200 mt-3 pt-3"></div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500 py-8">
              <div class="text-lg">No history</div>
              <div class="text-sm mt-2">Keine Änderungen protokolliert</div>
            </div>
            </div>
          </div>
        </div>

      </div>
      </div>
    </div>

    <!-- Floating Quick Navigation (mobile only) -->
    <div class="fixed bottom-4 right-4 z-40 lg:hidden">
      <div v-if="quickNavOpen" class="absolute bottom-14 right-0 bg-white rounded-lg shadow-xl border w-72 max-h-[70vh] overflow-y-auto">
        <div class="py-2">
          <button v-for="item in navSections" :key="item.id" @click="scrollToSection(item.id)" class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">{{ item.label }}</button>
        </div>
      </div>
      <button @click="quickNavOpen = !quickNavOpen" class="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
        <font-awesome-icon :icon="quickNavOpen ? 'times' : 'compass'" />
      </button>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6">
        <div class="text-lg font-semibold">Lade...</div>
      </div>
    </div>

    <SignaturePopup
      v-if="showFormPopup"
      :formType="currentFormType"
      :birthdate="customer.Geburtsdatum"
      :previewUrl="currentPreviewUrl"
      :requiresSignature="currentRequiresSignature"
      :customerId="customerId"
      @close="showFormPopup = false"
      @saved="onFormSaved"
    />
  </div>
</template>

<script>
import API from '@/API'
import SignaturePopup from '@/components/SignaturePopup.vue'
import { formatHistoryValue, hasMeaningfulHistoryChange } from '@/lib/history'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faArrowLeft, faPen, faCheckCircle, faUserSecret, faTrash, faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

library.add(faArrowLeft, faPen, faCheckCircle, faUserSecret, faTrash, faEye)

export default {
  name: 'CustomerEdit',
  components: {
    FontAwesomeIcon,
    SignaturePopup
  },
  data() {
    return {
      customerId: null,
      customer: {
        NachnameErziehungsberechtigter: '',
        VornameErziehungsberechtigter: '',
        Geburtsdatum: null,
        NachnameErziehungsberechtigter2: '',
        VornameErziehungsberechtigter2: '',
        Strasse: '',
        Hausnummer: '',
        Postleitzahl: '',
        Ort: '',
        Festnetz: '',
        MobilnummerEltern: '',
        MobilnummerEltern2: '',
        EmailEltern: '',
        EmailEltern2: '',
        Alleinerziehend: false,
        Bestand: false,
        Eingang: '',
        IBAN: '',
        BIC: '',
        Kontonummer: '',
        Bankleitzahl: '',
        AbwKontoinhaber: '',
        FibuKontonummer: '',
        Zuschussberechtigt: false,
        BUT: '',
        Art: '',
        BUTLaufzeit: '',
        Aktenzeichen: '',
        KoelnPass: '',
        BuchungszeitraumI: '',
        BuchungszeitraumII: '',
        Teilnehmerbetrag: '',
        Kostenstelle: '',
        Bemerkung: '',
        ZustimmungVeroeffentlichung1: '',
        ZustimmungVeroeffentlichung2: '',
        AbholungNotwendig: '',
        Adresstyp: '1',
        KennzHauptbankverbindung: '1',
        Lastschrift: '7',
        Mandantenbank: '2',
        Laenderkennzeichen: 'DE',
        Datenschutz: false,
        Fotoerklaerung: false,
        Status: '',
        ProfilKomplett: false,
        AdresseBestaetigt: false,
        NutztApp: false,
        admin: false,
        KeineInfomails: false,
        Ehrenamtsstatus: '',
      },
      originalCustomer: {},
      contracts: [],
      history: [],
      sentMails: [],
      ehrenamtForms: [],
      allEhrenamtFormTypes: [],
      availableFormConfigs: {}, // Map formType to config including requiresSignature
      beUrl: process.env.VUE_APP_BE_URL || '',
      loading: false,
      saving: false,
      confirmOrigin: null,
      // Signature popup state
      showFormPopup: false,
      currentFormType: '',
      currentPreviewUrl: '',
      currentRequiresSignature: true,
      quickNavOpen: false,
      collapsedSections: {
        contracts: false,
        stammdaten: false,
        aktionen: false,
        ehrenamt: false,
        emails: false,
        history: false
      },
    }
  },
  
  async mounted() {
    // Read customer ID from route params (e.g., /customers/123)
    this.customerId = this.$route.params.id ? parseInt(this.$route.params.id) : null
    
    // Check if we came from a contract
    const confirmOrigin = localStorage.getItem('confirmOrigin')
    if (confirmOrigin) {
      this.confirmOrigin = confirmOrigin
    }
    
    if (this.customerId) {
      await this.loadCustomer()
    }
  },
  
  computed: {
    meaningfulHistory() {
      return this.history.filter(version =>
        hasMeaningfulHistoryChange(version.field, version.oldValue, version.newValue)
      )
    },
    navSections() {
      const items = []
      if (this.customer.id) {
        items.push({ id: 'contracts', label: 'Verträge', icon: 'file' })
      }
      items.push({ id: 'stammdaten', label: 'Stammdaten', icon: 'edit' })
      if (this.customer.id) {
        items.push({ id: 'aktionen', label: 'Aktionen', icon: 'bolt' })
      }
      if (this.customer.Ehrenamtsstatus && this.allEhrenamtFormTypes.length > 0) {
        items.push({ id: 'ehrenamt', label: 'Ehrenamt Formulare', icon: 'user' })
      }
      items.push({ id: 'emails', label: 'E-Mails', icon: 'envelope' })
      items.push({ id: 'history', label: 'Letzte Änderungen', icon: 'history' })
      return items
    },
  },
  methods: {
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section]
    },
    scrollToSection(refName) {
      if (refName in this.collapsedSections) {
        this.collapsedSections[refName] = false
      }
      this.$nextTick(() => {
        const ref = this.$refs['section-' + refName]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
      this.quickNavOpen = false
    },
    async loadCustomer() {
      this.loading = true
      try {
        const response = await new Promise((resolve, reject) => {
          API.getCustomer(this.customerId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load customer'))
            }
          })
        })
        
        // Convert dates from backend
        const processedResponse = this.convertDatesFromBackend(response)
        this.customer = processedResponse
        this.originalCustomer = JSON.parse(JSON.stringify(this.customer))
        
        this.contracts = response.contracts || []
        
        // Load sent mails
        await this.loadSentMails()
        
        // Load ehrenamt forms
        await this.loadEhrenamtForms()
        
        // Load history separately
        await this.reloadHistory()
        
      } catch (error) {
        console.error('Error loading customer:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Laden des Kunden',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.loading = false
      }
    },
    
    convertDatesFromBackend(data) {
      const dateFields = ['Geburtsdatum']
      
      const converted = { ...data }
      
      dateFields.forEach(field => {
        if (converted[field]) {
          // Convert ISO date (YYYY-MM-DD) to date input format
          if (converted[field].includes('T')) {
            converted[field] = converted[field].split('T')[0]
          }
        }
      })
      
      return converted
    },
    
    async saveCustomer() {
      this.saving = true
      try {
        const customerData = { ...this.customer }
        
        // Convert empty date strings to null
        if (customerData.Geburtsdatum === '') {
          customerData.Geburtsdatum = null
        }
        
        const response = await new Promise((resolve, reject) => {
          if (this.customerId) {
            API.updateCustomer(this.customerId, customerData, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(data && data.error ? data.error : 'Failed to update customer')
              }
            })
          } else {
            API.createCustomer(customerData, (data, status) => {
              if (status === 201) {
                resolve(data)
              } else {
                reject(data && data.error ? data.error : 'Failed to create customer')
              }
            })
          }
        })
        
        // Convert dates from backend response before updating customer
        const processedResponse = this.convertDatesFromBackend(response)
        this.customer = { ...this.customer, ...processedResponse }
        this.originalCustomer = JSON.parse(JSON.stringify(this.customer))
        
        if (!this.customerId) {
          this.customerId = response.id
          this.$router.replace({ query: { ...this.$route.query, id: this.customerId } })
          await this.loadCustomer()
        } else {
          // For existing customers, reload the history to show the new change
          await this.reloadHistory()
        }
        
        this.$toast?.open?.({
          message: 'Kunde gespeichert',
          type: 'success',
          position: 'top'
        })
        
      } catch (error) {
        console.error('Error saving customer:', error)
        // Try to get the specific error message from the API response
        let errorMessage = 'Fehler beim Speichern des Kunden'
        if (error && error.message) {
          errorMessage = error.message
        } else if (error && typeof error === 'string') {
          errorMessage = error
        }
        this.$toast?.open?.({
          message: errorMessage,
          type: 'error',
          position: 'top'
        })
      } finally {
        this.saving = false
      }
    },
    
    async saveAndClose() {
      await this.saveCustomer()
      if (!this.saving) {
        this.goBack()
      }
    },
    
    async confirmCustomer() {
      try {
        await new Promise((resolve, reject) => {
          API.performCustomerAction(this.customerId, { action: 'Bestätigen' }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to confirm customer'))
            }
          })
        })
        
        this.customer.Status = 'Bestätigt'
        this.$toast?.open?.({
          message: 'Kunde bestätigt',
          type: 'success',
          position: 'top'
        })

        let confirmOrigin = localStorage.getItem("confirmOrigin")
        if (confirmOrigin) {
          localStorage.removeItem("confirmOrigin")
          window.location = confirmOrigin
        } else {
          await this.reloadHistory()
        }
        
      } catch (error) {
        console.error('Error confirming customer:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Bestätigen des Kunden',
          type: 'error',
          position: 'top'
        })
      }
    },
    
    async confirmEhrenamtsstatus(editMail = false) {
      try {
        const response = await new Promise((resolve, reject) => {
          API.performCustomerAction(this.customerId, { action: 'Bestätigen Ehrenamtsstatus', editMail }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to confirm Ehrenamtsstatus'))
            }
          })
        })
        
        // If mailto link is returned, open it in new window
        if (response && response.mailto) {
          window.open(response.mailto, '_blank')
        }
        
        this.customer.Ehrenamtsstatus = 'Bestätigt'
        this.$toast?.open?.({
          message: (response && response.message) || 'Ehrenamtsstatus bestätigt',
          type: 'success',
          position: 'top'
        })
        
        // Reload history to show the change
        await this.reloadHistory()
        
      } catch (error) {
        console.error('Error confirming Ehrenamtsstatus:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Bestätigen des Ehrenamtsstatus',
          type: 'error',
          position: 'top'
        })
      }
    },
    
    async assumeIdentity() {
      if (!confirm('Möchten Sie die Identität dieses Kunden annehmen? Sie werden als dieser Kunde angemeldet.')) {
        return
      }
      
      window.location = process.env.VUE_APP_BE_URL + `/customers/${this.customerId}/assumeIdentity`
    },
    
    getCookie(name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) return parts.pop().split(';').shift()
      return null
    },
    
    async reloadHistory() {
      console.log('Starting history reload for customer:', this.customerId)
      
      // Add a short delay to ensure the backend has processed the change
      await new Promise(resolve => setTimeout(resolve, 500))
      
      console.log('Delay completed, calling API.getCustomerHistory')
      
      try {
        const response = await new Promise((resolve, reject) => {
          console.log('Making API call to getCustomerHistory')
          API.getCustomerHistory(this.customerId, (data, status) => {
            console.log('API.getCustomerHistory response:', { data, status })
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error(`Failed to load history: ${status}`))
            }
          })
        })
        
        console.log('History API response:', response)
        this.history = response || []
        console.log('History reloaded successfully:', this.history)
        
      } catch (error) {
        console.error('Error reloading history:', error)
      }
    },
    
    async loadEhrenamtForms() {
      try {
        // Load available form types with config
        const availableForms = await new Promise((resolve, reject) => {
          API.getEhrenamtAvailableForms(this.customerId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load available ehrenamt forms'))
            }
          })
        })
        this.allEhrenamtFormTypes = availableForms.map(f => f.formType)
        // Store config by formType
        availableForms.forEach(f => {
          this.availableFormConfigs[f.formType] = f
        })

        // Load customer's submitted forms
        const response = await new Promise((resolve, reject) => {
          API.getEhrenamtForms(this.customerId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load ehrenamt forms'))
            }
          })
        })
        this.ehrenamtForms = response || []
      } catch (error) {
        console.error('Error loading ehrenamt forms:', error)
      }
    },

    openFormFiller(formType) {
      const formConfig = this.availableFormConfigs[formType]
      this.currentFormType = formType
      this.currentPreviewUrl = `${this.beUrl}/customers/${this.customerId}/ehrenamt/preview?form_type=${encodeURIComponent(formType)}`
      this.currentRequiresSignature = formConfig ? formConfig.requiresSignature : true
      this.showFormPopup = true
    },

    onFormSaved() {
      this.showFormPopup = false
      this.loadEhrenamtForms() // Reload forms to show the completed status
    },

    resetForm(formType) {
      if (!confirm(`Möchten Sie das Formular "${formType}" wirklich zurücksetzen?`)) {
        return
      }
      const formId = this.getFormId(formType)
      API.deleteEhrenamtForm(formId, (data, status) => {
        if (status === 200) {
          this.loadEhrenamtForms() // Reload forms to show the updated status
        } else {
          alert('Fehler beim Zurücksetzen des Formulars.')
        }
      })
    },
    
    loadSentMails() {
      API.getCustomerSentMails(this.customerId, (res, status) => {
        if (status === 200) {
          this.sentMails = res.emails || []
        }
      })
    },
    
    isFormDone(formType) {
      return this.ehrenamtForms.some(f => f.formType === formType && f.status === 'done')
    },
    getFormId(formType) {
      const form = this.ehrenamtForms.find(f => f.formType === formType && f.status === 'done')
      return form ? form.id : ''
    },
    
    createContract() {
      this.$router.push({ path: '/contracts/edit', query: { customerID: this.customerId } })
    },
    
    editContract(contractId) {
      this.$router.push({ path: '/contracts/edit', query: { id: contractId } })
    },

    editContractNew(contractId) {
      this.$router.push(`/contracts/${contractId}`)
    },
    
    goBack() {
      const getCookie = (name) => {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) return parts.pop().split(';').shift()
        return null
      }
      
      const tableReferrer = getCookie('tableReferrer')
      const destination = tableReferrer || '/customers'
      window.location = destination
    },
    
    goBackToContract() {
      if (this.confirmOrigin) {
        localStorage.removeItem('confirmOrigin')
        window.location = this.confirmOrigin
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('de-DE')
    },
    
    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    formatValue(value, fieldName) {
      if (value === true) return 'Ja'
      if (value === false) return 'Nein'
      if (value === null || value === undefined) return ''
      return formatHistoryValue(fieldName, value)
    },
    
    formatFieldName(fieldName) {
      const fieldTranslations = {
        'NachnameErziehungsberechtigter': 'Nachname Erziehungsberechtigter',
        'VornameErziehungsberechtigter': 'Vorname Erziehungsberechtigter',
        'Geburtsdatum': 'Geburtsdatum',
        'NachnameErziehungsberechtigter2': 'Nachname Erziehungsberechtigter 2',
        'VornameErziehungsberechtigter2': 'Vorname Erziehungsberechtigter 2',
        'Strasse': 'Straße',
        'Hausnummer': 'Hausnummer',
        'Postleitzahl': 'Postleitzahl',
        'Ort': 'Ort',
        'Festnetz': 'Festnetz',
        'MobilnummerEltern': 'Mobilnummer Eltern',
        'MobilnummerEltern2': 'Mobilnummer Eltern 2',
        'EmailEltern': 'E-Mail Eltern',
        'EmailEltern2': 'E-Mail Eltern 2',
        'Alleinerziehend': 'Alleinerziehend',
        'IBAN': 'IBAN',
        'BIC': 'BIC',
        'Status': 'Status',
        'Datenschutz': 'Datenschutz',
        'Fotoerklaerung': 'Fotoerklärung',
      }
      
      return fieldTranslations[fieldName] || fieldName
    },
  }
}
</script>

<style scoped>
input[type="text"],
input[type="email"],
input[type="date"],
input[type="number"],
select,
textarea {
  @apply focus:outline-none focus:ring-2 focus:ring-blue-500;
}

@media (max-width: 1023px) {
  input[type="date"]::-webkit-calendar-picker-indicator {
    display: none;
  }
  input[type="date"] {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    padding-left: 4px;
    padding-right: 4px;
  }
}
</style>
